import api from '@/api'
import tableHeaders from '@/utils/tableHeaders'
import format from '@/utils/format'
import { toFixedHalfUp } from '@/utils/round'
import { AIRFormula, DISPOSALFormula, SOILFormula, WATERFormula } from "@/utils/journal";
import { isPhenolTest, getPhenolDisplayName, PHENOL_PRIMARY_ID, mergePhenolSampleLists } from '@/utils/pheonl';
import { alignSampleListToColumns } from '@/utils/resultSampleOrder';
import { deriveDisposalJournalResults, DISPOSAL_REPORT_GROUP_IDS, expandDisposalCategoryBySelectionOrder, mergeDisposalSelectionLabels } from '@/utils/disposalJournalDerive';

/** 0이 유효 측정값인 시험항목 — 평균 결과가 0이어도 '불검출'로 치환하지 않음
 * release 핫픽스 회수 (217819b): 중탄산/탄산·알칼리도·총대장균군 등 0 유효값 수질 항목
 */
const ZERO_VALID_TEST_IDS = [
	'WATER_TEST_080',
	'WATER_TEST_031',
	'WATER_TEST_112',
	'WATER_TEST_119',
	'WATER_TEST_183',
	'WATER_TEST_184',
	'WATER_TEST_185',
	'WATER_TEST_179',
	'WATER_TEST_180',
	'WATER_TEST_181',
	'WATER_TEST_182',
	'WATER_TEST_150',
	'WATER_TEST_151',
]

function averagePluralSamples(sampleList, testId = null) {
	const isPluralNo = (no) => no?.includes('\u00A0');
	const cleanNo = (no) => (no || '').replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '').trim();
	const baseNoOf = (no) => cleanNo(no).replace(/-\d+$/, '');
	const parseVal = (v) => {
		if (v === '불검출') return 0;
		const n = parseFloat(v);
		return isNaN(n) ? null : n;
	};
	const getDecimalPlaces = (v) => {
		const s = String(v);
		const i = s.indexOf('.');
		return i >= 0 ? s.length - i - 1 : 0;
	};

	const isZeroValid = testId != null && ZERO_VALID_TEST_IDS.includes(testId);

	const pluralMap = {};
	sampleList.forEach((item, idx) => {
		if (isPluralNo(item.no)) {
			const bNo = baseNoOf(item.no);
			if (!pluralMap[bNo]) pluralMap[bNo] = [];
			pluralMap[bNo].push(idx);
		}
	});

	if (Object.keys(pluralMap).length === 0) return sampleList;

	for (const bNo in pluralMap) {
		const baseSample = sampleList.find(s => !isPluralNo(s.no) && cleanNo(s.no) === bNo);
		if (!baseSample) continue;

		const pluralSamples = pluralMap[bNo].map(idx => sampleList[idx]);
		const contributing = [baseSample, ...pluralSamples].map(s => s.value);
		const allValues = contributing.map(parseVal);
		const validValues = allValues.filter(v => v !== null);

		if (validValues.length >= 2) {
			// 평균 대상이 전부 문자열 '불검출'이면 zero-valid 여부와 무관하게 '불검출' 유지
			// (parseVal이 '불검출'을 0으로 변환해 실측 0과 구분되지 않는 문제 보정)
			const allUndetected =
				contributing.filter(v => v === '불검출').length === validValues.length;

			if (allUndetected) {
				baseSample.value = '불검출';
			} else {
				const maxDecimals = [baseSample, ...pluralSamples]
					.map(s => parseVal(s.value) !== null ? getDecimalPlaces(s.value) : 0)
					.reduce((a, b) => Math.max(a, b), 0);

				const avg = validValues.reduce((sum, v) => sum + v, 0) / validValues.length;
				baseSample.value = (!isZeroValid && Number(avg) === 0) ? '불검출' : toFixedHalfUp(avg, maxDecimals);
			}
		}
	}

	const removeIndices = new Set(Object.values(pluralMap).flat());
	return sampleList.filter((_, idx) => !removeIndices.has(idx));
}

// 헤더 변환 공통 함수
const transformHeader = (header, options = {}) => {
	header.key = header.headerKey;
	header.label = header.text;
	if (options.includeFormatter) {
		header.formatter = (value, key) => {
			if (key.indexOf('Date') >= 0) {
				return format.date({ date: value, onlyDate: true });
			}
			return value;
		};
	}
	if (options.disabled !== undefined && header.headerKey === 'delete') {
		header.disabled = options.disabled;
	}
	return header;
};

const state = {
	referral: {
		headers: [],
		list: [],
		count: 1,
	},
	journal: {
		headers: [],
		list: [],
		count: 1,
	},
	journalResult: [],
	journalList: [],
	journalForm: {
		testDate: '',
		machine : {
			name: '',
			data: []
		},
		testMachineCondition: [],
		testMethod: '',
		method: [],
	},
	testMachineHeaders: [],
	machineList: [],
	journalData: {},
	report: {
		headers: [],
		list: [],
	},
	testResult: {
		hasSavedResult: null,
		approvalDatetime: null,
		endDate: null,
		humidityMax: null,
		humidityMin: null,
		rejectDatetime: "",
		requestDate: "",
		startDate: "",
		status : "",
		temperatureMax : "",
		temperatureMin : "",
		url : null,
		headers: [],
		list: [],
		isProper: ''
	},
	testResultRequestSeq: 0,
	environment: {
		temperature: '',
		humidity: '',
		note: ''
	},
	excelEnvironment: {
		minHumidity: '',
		maxHumidity: '',
		minTemperature: '',
		maxTemperature: '',
	},	
	sampleResultList: [],
	document: {
		headers: [],
		list: [],
	},
	isTestMap: {}
}

const getters = {
	referral: (state, getters, rootState, rootGetters) => {
		const isStatusComplete = rootGetters['project/detail/isStatusComplete'];
		state.referral.headers = state.referral.headers.map(header => 
			transformHeader(header, { includeFormatter: true, disabled: isStatusComplete })
		);
		return state.referral;
	},
	journal: state => {
		state.journal.headers = state.journal.headers.map(header => 
			transformHeader(header, { includeFormatter: true })
		);
		return state.journal;
	},
	journalList: state => {
		return state.journalList.map(item => {
			item.text = `${item.testName.name}(${item.projectCode})`;
			item.value = item.testId;
			return item;
		});
	},
	journalResult: state => state.journalResult,
	journalForm: state => state.journalForm,
	machineList: state => {
		return state.machineList.map(item => ({
			text: item.name,
			value: item.data
		}));
	},
	testMachineHeaders: state => {
		return tableHeaders.useDeviceTableHeader.map(header => 
			transformHeader(header)
		);
	},
	journalData: state => state.journalData,
	report: state => {
		state.report.headers = state.report.headers.map(header => 
			transformHeader(header, { includeFormatter: true })
		);
		return state.report;
	},
	testResult: state => {
		state.testResult.headers = state.testResult.headers.map(header => 
			transformHeader(header)
		);
		return state.testResult;
	},
	environment: state => state.environment,
	excelEnvironment: state => state.excelEnvironment,
	isTestMap: state => state.isTestMap,
	sampleResultList: state => state.sampleResultList,
	document: state => {
		state.document.headers = state.document.headers.map(header => 
			transformHeader(header, { includeFormatter: true })
		);
		return state.document;
	}
}

const mutations = {
	setEnvironment: (state, data) => {
		state.environment = {...state.environment, ...data}
	},
	// 이전 요청 필드가 남지 않도록 병합이 아닌 치환(기본값 + API payload)
	setExcelEnvironment: (state, data) => {
		state.excelEnvironment = {
			minHumidity: '',
			maxHumidity: '',
			minTemperature: '',
			maxTemperature: '',
			...data
		};
	},
	setReferral: (state, data) => {
		state.referral.headers = [...tableHeaders.testRequestTableHeader];
		// displayNo — 화면 표시용 순번. 내부 id 를 사용자에게 노출하지 않는다(3-4).
		state.referral.list = data.res.list.map((el, idx) => ({
			...el,
			displayNo: idx + 1,
			url: `/v1/project/${data.id}/referral/${el.id}`
		}));
		state.referral.count = data.res.list.length;
	},
	setJournalList: (state, data) => {
		// 현재 사용되지 않음
	},
	setJournalResult: (state, data) => {
		state.journalResult = {...data}
	},
	setJournalForm: (state, data) => {
		state.journalForm = {...state.journalForm, ...data};
		state.journalForm.testMethod = state.journalForm.testMethod || state.journalForm.method[0];
		
		const processFormData = (form) => {
			form.form.forEach(item => {
				item.key = item.headerKey;
				item.label = item.text;
				item.readonly = (item.headerKey === 'lastConcentration' || item.headerKey === 'markedConcentration');
			});
			if (form.key === 'useReagent' && state.journalForm.reagents) {
				state.journalForm.useReagent = [...state.journalForm.reagents];
			}
		};
		
		state.journalForm.form_data[0].forEach(processFormData);
		state.journalForm.form_data[1].forEach(processFormData);
	},
	resetJournalForm: (state) => {
		state.journalForm = {
			testDate: '',
			machine : {
				name: '',
				data: []
			},
			testMachineCondition: [],
			testMethod: '',
			method: [],
		}
		state.environment = {
			temperature: '',
			humidity: '',
			note: ''
		}
	},
	resetTestResult: (state) => {
		state.testResultRequestSeq += 1;
		state.testResult = {
			hasSavedResult: null,
			approvalDatetime: null,
			endDate: null,
			humidityMax: null,
			humidityMin: null,
			rejectDatetime: "",
			requestDate: "",
			startDate: "",
			status : "",
			temperatureMax : "",
			temperatureMin : "",
			url : null,
			headers: [],
			list: [],
			isProper: ''
		}
	},
	setMachine: (state, data) => {
		state.machineList = [...data];
	},
	setMatchJournalData: (state, data) => {
		if (!data.test_id) {
			return;
		}
		
		let list = [];
		if (data.test_id.indexOf('SOIL') >= 0) {
			list = SOILFormula;
		} else if (data.test_id.indexOf('WATER') >= 0) {
			list = WATERFormula;
		} else if (data.test_id.indexOf('AIR') >= 0) {
			list = AIRFormula;
		} else if (data.test_id.indexOf('DISPOSAL') >= 0) {
			list = DISPOSALFormula;
		}
		
		if (list.length > 0) {
			const result = list.find(el => el.category === data.name);
			state.journalData = result ? {...result} : {};
		}
	},
	setReport: (state, data) => {
		state.report.headers = [...tableHeaders.testResultTableHeader];
		state.report.list = data.list.map((el, idx) => ({
			...el,
			displayNo: idx + 1,
			url: `/v1/project/test/report/${el.id}`
		}));
	},
	setTestResult: (state,data) => {
		const {result, category, sampleList, latestPhenolTestId} = data;
		// 1) 헤더 정의 — DISPOSAL 은 수질과 동일한 관련기준(고객사 시료리스트 법정기준) 양식을 탄다
		if(category.type === 'WATER' || category.type === 'DISPOSAL') {
			state.testResult.headers = [
				{ headerKey: 'testName', text: '시료 이름' },
				{ headerKey: 'testResultUnit', text: '단위' },
				{ headerKey: 'criteria_area', text: '관련기준', form: [
					{text: '', headerKey: 'standard' },
				]},
				{ headerKey: 'sampleList', text: '검체명', form: [] },
			]
		} else {
			state.testResult.headers = [
				{ headerKey: 'testName', text: '시료 이름' },
				{ headerKey: 'testResultUnit', text: '단위' },
				{ headerKey: 'criteria_area', text: '오염기준', form: [
					{text: '1지역', headerKey: 'criteria_area_1' },
					{text: '2지역', headerKey: 'criteria_area_2' },
					{text: '3지역', headerKey: 'criteria_area_3' },
				]},
				{ headerKey: 'sampleList', text: '검체명', form: [] },
			];
		}
		state.testResult.list = [];

		//2025.03.20 추가 : 시험결과 확인 리스트 시험성적서와 동일하게 보이도록 설정.
		//기준 1. testResult내 각 시험항목의 isTest 가 하나라도 true 여야만 노출
		const showIdsSet = new Set();
		const isTestMap = {};
		sampleList.forEach(sample => {
			if (!sample.testResult) return;
			Object.entries(sample.testResult).forEach(([key, testInfo]) => {
				if (testInfo.isTest) {
					showIdsSet.add(key);
				}
				if (!isTestMap[key]) isTestMap[key] = {};
				isTestMap[key][sample.sampleNo] = !!testInfo.isTest;
			});
		});
		state.isTestMap = isTestMap;
		const showIds = [...showIdsSet];

		//기준 2. showId 포함 + hideGroup 인 데이터는 안보이도록 처리.
		// DISPOSAL: 수질처럼 sample.categories 선택 순서로 전개 (원소분석 하위 포함)
		const fullCategoryList = [...category.list];
		if (category.type === 'DISPOSAL') {
			category.list = expandDisposalCategoryBySelectionOrder(fullCategoryList, {
				categories: mergeDisposalSelectionLabels(sampleList),
				showIds,
				enforceDocumentHide: true,
			});
		} else {
			category.list = category.list
				.filter(el => showIds.includes(el.testId))
				.filter(el => {
					if (el.hide_type === 'hideGroup' && el.testId !== 'SOIL_TEST_016') {
						el.type = 'test';
					}
					return el.is_document_hide === 0;
				});
		}

		//3. 복수시료 평균 처리: 각 시험항목의 sampleList에서 복수시료를 평균값으로 병합
		result.forEach(item => {
			if (item.sampleList) {
				item.sampleList = averagePluralSamples(item.sampleList, item.testId);
			}
		});

		//3-1. 수질 페놀류 병합: 027/102 result를 하나로 합치고 category.list에서 102 제거
		if (category.type === 'WATER') {
			const hasPhenolInShowIds = showIds.some(id => isPhenolTest(id));
			const primaryResult = result.find(r => r.testId === PHENOL_PRIMARY_ID);
			const secondaryResult = result.find(r => isPhenolTest(r.testId) && r.testId !== PHENOL_PRIMARY_ID);

			if (primaryResult && secondaryResult && primaryResult.sampleList && secondaryResult.sampleList) {
				// @/utils/pheonl — Report·ModalResult 공통 병합 규칙
				primaryResult.sampleList = mergePhenolSampleLists(
					primaryResult.sampleList,
					secondaryResult.sampleList,
					{ latestPhenolTestId, showIds: showIdsSet }
				);
			} else if (primaryResult && secondaryResult && !primaryResult.sampleList && secondaryResult.sampleList) {
				primaryResult.sampleList = secondaryResult.sampleList;
			} else if (!primaryResult && secondaryResult) {
				secondaryResult.testId = PHENOL_PRIMARY_ID;
				secondaryResult.testName = getPhenolDisplayName(secondaryResult.testId, secondaryResult.testName, 'WATER');
			}

			category.list = category.list.filter(el => !(isPhenolTest(el.testId) && el.testId !== PHENOL_PRIMARY_ID));
			if (hasPhenolInShowIds && !category.list.some(el => el.testId === PHENOL_PRIMARY_ID)) {
				const phenolCategory = fullCategoryList.find(el => el.testId === PHENOL_PRIMARY_ID);
				if (phenolCategory) {
					// push 대신 fullCategoryList 원래 순서 기준으로 올바른 위치에 삽입
					// (push를 쓰면 직접법 전환 시 페놀류가 맨 마지막 행으로 밀림)
					const phenolOriginalIdx = fullCategoryList.findIndex(el => el.testId === PHENOL_PRIMARY_ID);
					const insertIdx = category.list.findIndex(el => {
						const elOriginalIdx = fullCategoryList.findIndex(f => f.testId === el.testId);
						return elOriginalIdx > phenolOriginalIdx;
					});
					if (insertIdx === -1) {
						category.list.push({ ...phenolCategory });
					} else {
						category.list.splice(insertIdx, 0, { ...phenolCategory });
					}
				}
			}
		}

		//4. 시험항목 (category) 기준으로 순서 세팅
		// 폐기물 그룹(부모) 표기 전용 — 호출부가 DISPOSAL 분기 안에 있다.
		const itemLabel = (cat) => {
			if (category.type === 'DISPOSAL') {
				return cat?.search_name || cat?.name || cat?.lab_name || '';
			}
			return cat?.name || cat?.lab_name || '';
		};
		const orderList = [];
		category.list.forEach(item => {
			const found = result.find(r => r.testId === item.testId);
			if (found) {
				found.criteria_area_1 = item.criteria_area_1;
				found.criteria_area_2 = item.criteria_area_2;
				found.criteria_area_3 = item.criteria_area_3;
				// 폐기물만 화면에서 이름을 다시 정한다.
				// 토양·수질·대기는 서버가 준 testName 을 그대로 쓴다 — 서버 getName 규칙이
				// (수질=name, 그 외=search_name) 이관 전 기준이라, 여기서 name 으로 덮으면
				// 토양 결과통보서가 '비소' 대신 'As' 로 나와 실제 시스템과 어긋난다.
				if (category.type === 'DISPOSAL') {
					found.testName =
						item.search_name || item.name || item.lab_name || found.testName || '';
				}
				const disposalGroupParent = item.hide_parent_id || item._derivedFrom || item.parent_id;
				if (category.type === 'DISPOSAL' && DISPOSAL_REPORT_GROUP_IDS.includes(disposalGroupParent)) {
					const parent = fullCategoryList.find(c => c.testId === disposalGroupParent);
					if (parent) {
						found.groupName = itemLabel(parent);
						found.groupKey = disposalGroupParent;
					}
				}
				if (item.subGroupName) {
					found.subGroupName = item.subGroupName;
				}
				orderList.push(found);
			}
		});
		if (result.length > 0) {
			result[0].sampleList.forEach((item, idx) => {
				state.testResult.headers[3].form.push({
					text: item.name,
					headerKey: `sampleItem${idx}`
				});
			});
			const nameIdx = result[0].sampleList.length - 1;
			// 표는 값을 위치로 열에 넣으므로, 행마다 시료번호로 열 순서에 맞춘다 (EG-2610020 페놀류 어긋남)
			const columns = result[0].sampleList;
			orderList.forEach(item => {
				if (Array.isArray(item.sampleList)) {
					item.sampleList = alignSampleListToColumns(item.sampleList, columns);
				}
				if (category.type === 'WATER' && isPhenolTest(item.testId)) {
					item.testName = getPhenolDisplayName(item.testId, item.testName, 'WATER');
				}
				state.testResult.list.push({
					key: `sampleItem${nameIdx}`,
					...item
				});
			});
		}
	},
	setTestResultInfo: (state, data) => {
		state.testResult = {
			...state.testResult,
			...data,
			hasSavedResult: typeof data?.hasSavedResult === 'boolean' ? data.hasSavedResult : null
		}
	},
	beginTestResultRequest: (state) => {
		state.testResultRequestSeq += 1;
	},
	setSampleResultList: (state, data) => {
		state.sampleResultList = [...state.sampleResultList, ...data]
	},
	setDocument: (state, data) => {
		state.document.headers = [...tableHeaders.documentTableHeader];
		state.document.list = (data || []).map((el, idx) => ({ ...el, displayNo: idx + 1 }));
	}
}

const actions = {
	//산출문서 - 시험의뢰서 가져오기
	loadReferral: async ({state, commit}, id) => {
		const res = await api.project.getReferral(id);
		commit('setReferral', {res: res, id: id});
	},
	//산출문서 - 시험의뢰서 개별 다운로드
	downloadReferral: async ({state, commit}, payload) => {
		const {projectId, id} = payload;
		const res = await api.project.downloadReferral(projectId, id);
		return res;
	},
	//산출문서 - 시험의뢰서 생성하기
	addReferral: async ({commit, dispatch}, id) => {
		const res = await api.project.createReferral(id);
		dispatch('loadReferral', id);
		return res;
	},
	//산출문서 - 시험의뢰서 개별 삭제하기
	deleteReferral: async({commit, dispatch}, payload) => {
		const {projectId, id} = payload;
		const res = await api.project.deleteReferral(projectId, id);
		dispatch('loadReferral', projectId);
		return res;
	},
	//시험일지 - 시험일지 목록 가져오기
	loadJournalList: async({state, commit}, id) => {
		const res = await api.project.getTestJournal(id);
		commit('setJournalList', res);
	},
	//시험일지 - 입력된 시험 결과 가져오기
	loadJournalResult: async({state,commit}, payload) => {
		const {id, testId, name} = payload;
		const res = await api.project.getResult(id, testId);
		commit('setJournalResult', res);
		commit('setMatchJournalData', {...res, name: name});
	},
	//시험일지 - 시험결과 저장하기
	updateJournalResult: async({state, commit}, payload) => {
		const {projectId, testId, body} = payload;
		try {
			const res = await api.project.updateJournalResult(projectId, testId, body);
			commit('resetJournalForm');
			return res;
		} catch(e) {
			return false;
		}
	},
	//시험일지 - 일지 form data 가져오기
	loadJournalForm: async ({state, commit}, id) => {
		const res = await api.project.getJournalForm(id);
		commit('setJournalForm', res);
	},
	//시험일지 - 기기리스트 가져오기
	loadMachine: async({state, commit}) => {
		const res = await api.project.getMachine();
		commit('setMachine', res);
	},
	//시험일지 - 입력해야하는 시료 목록 가져오기
	loadSampleResultList: async({state, commit}, payload) => {
		const {projectId, testId} = payload;
		const res = await api.project.getSampleResultList(projectId, testId);
		commit('setSampleResultList', res);
	},
	loadReport: async({state, commit}, projectId) => {
		const res = await api.project.getReport(projectId);
		commit('setReport', res);
	},
	//산출문서 - 시험성적서
	loadDocument: async({state, commit}, projectId) => {
		const res = await api.project.getDocument(projectId);
		commit('setDocument', res);
	},
	reportDocument: async({commit, dispatch}, {id, payload, mode}) => {
		const res = await api.project.reportDocument(id, payload, mode);
		dispatch('loadDocument', id);
		return res;
	},
	deleteDocument: async({state, dispatch}, {projectId, documentId}) => {
		const res = await api.project.deleteDocument(documentId);
		dispatch('loadDocument', projectId);
		return res;
	},
	downloadReport: async({state, commit}, id) => {
		const res = await api.project.downloadReport(id);
		return res;
	},
	deleteReport: async({commit, dispatch}, payload) => {
		const {projectId, reportId} = payload;
		const res = await api.project.deleteReport(reportId);
		dispatch('loadReport', projectId);
		return res;
	},
	//산출문서 - 시험결과 확인하기 가져오기
	loadTestResult: async({state, commit}, id) => {
		commit('beginTestResultRequest');
		const requestSeq = state.testResultRequestSeq;
		const result = await api.project.getTestResult(id);
		const info = await api.project.getTestResultInfo(id);
		const category = await api.project.getCategory(id, null, "result");
		const sampleList = await api.project.getSampleList(id, false);
		// 폐기물: 삼성분/원소분석/물리적조성 파생행 반영.
		// 결과확인창·결과통보서는 삼성분 소계(__TOTAL) 미표시 (시험성적서만 소계).
		if (category?.type === 'DISPOSAL') {
			await deriveDisposalJournalResults(id, category, result, sampleList, {
				includeSubtotal: false,
			});
		}
		// 페놀류 최신 시험방법 판단: 시험일지 목록의 createDatetime이 가장 최신인 testId 사용
		const reportRes = await api.project.getReport(id);
		const phenolReports = (reportRes?.list || []).filter(r => isPhenolTest(r.testId));
		let latestPhenolTestId = null;
		if (phenolReports.length > 0) {
			const latest = phenolReports.reduce((a, b) =>
				new Date(a.createDatetime) > new Date(b.createDatetime) ? a : b
			);
			latestPhenolTestId = latest.testId;
		}
		if (requestSeq !== state.testResultRequestSeq) return false;
		commit('setTestResult', {result, category, sampleList, latestPhenolTestId});
		commit('setTestResultInfo', info);
		return true;
	},
	//산출문서 - 시험결과 정보 가져오기
	loadTestResultInfo: async({state, commit}, id) => {
		commit('beginTestResultRequest');
		const requestSeq = state.testResultRequestSeq;
		const res = await api.project.getTestResultInfo(id);
		if (requestSeq !== state.testResultRequestSeq) return false;
		commit('setTestResultInfo', res);
		return true;
	},
	//산출문서 - 시험결과 정보 저장하기
	editTestResultInfo: async({state, commit, dispatch}, {id, body}) => {
		const requestSeq = state.testResultRequestSeq;
		const payload = {...body};
		delete payload.hasSavedResult;
		const res = await api.project.editTestResultInfo(id, payload);
		if (requestSeq === state.testResultRequestSeq) dispatch('loadTestResult', id);
		return res;
	},
	//시험일지 - 연구실 환경값 가져오기
	loadEnvironment: async({commit}, params) => {
		const res = await api.project.getEnvironment(params);
		commit('setEnvironment', res);
		return res;
	},
	//시험결과 확인 - 엑셀시트에 입력된 연구실 환경값 가져오기
	// payload: projectId | { id, category?: 'SOIL'|'WATER'|'DISPOSAL' } (excel-table; 서버는 `api.project.loadExcelEnvironment(id, params)`와 동일)
	loadExcelEnvironment: async({commit}, payload) => {
		const id = typeof payload === 'object' && payload !== null ? payload.id : payload;
		const params = {};
		if (typeof payload === 'object' && payload !== null && ['SOIL', 'WATER', 'DISPOSAL'].includes(payload.category)) {
			params.category = payload.category;
		}
		const res = await api.project.loadExcelEnvironment(id, params);
		commit('setExcelEnvironment', res);
		return res;
	}
}

export default { namespaced: true, state, getters, mutations, actions }
